import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { createTestWorkerCommandClient, sendTestWorkerCommand, TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { readableMobileError, selectEmployeeShift, tasksForShift } from '../../runtime/mobile/employeeRuntimeState';
import { colors, spacing, typography } from '../../config/theme';
import { Card, ErrorBanner, GhostButton, Loader, Pill, PrimaryButton, SectionLabel, TextButton, screen } from '../ui';
import { Sticker, StickerBackdrop } from '../brand';
import { PHASE, isShiftTerminal, primaryAction, shiftStateLabel, taskStateLabel } from './shiftLabels';
import TaskSheet from './TaskSheet';
import HandoverSheet from './HandoverSheet';
import TelegramLinkCard, { TelegramLinkedNote } from './TelegramLinkCard';

// Completing a task can carry several photos (a few MB) and the server then
// forwards each to Telegram — give it room and a single safe retry.
const photoCommandClient = createTestWorkerCommandClient({ timeoutMs: 90000, maxAttempts: 2 });

// Best-effort geo check-in right after a shift opens. Never throws, never
// blocks: if the browser has no geolocation, the employee denies it, or it
// times out, we simply skip. The Worker records the distance for the owner.
function reportCheckInGeo(user, shiftId) {
  try {
    const geo = typeof navigator !== 'undefined' && navigator.geolocation;
    if (!geo) return;
    geo.getCurrentPosition(
      async (pos) => {
        try {
          const token = await user.getIdToken();
          await fetch(`${TEST_WORKER_URL}/checkin/geo`, {
            method: 'POST',
            headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
            body: JSON.stringify({
              shiftId,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            }),
          });
        } catch { /* ignore */ }
      },
      () => { /* denied / unavailable — skip silently */ },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  } catch { /* ignore */ }
}

const toMs = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : v instanceof Date ? v.getTime() : Date.parse(v));
const hhmm = (v) => { const d = new Date(toMs(v)); return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const taskKind = (status) => (status === 'completed' ? 'good' : status === 'overdue' ? 'bad' : status === 'due_soon' ? 'warn' : 'muted');

export default function EmployeeHomeScreen() {
  const { user, profile, logout } = useAuth();
  // An inactive profile cannot read operational collections (security rules), so
  // hold the subscriptions until an owner activates the account.
  const activeProfile = profile?.active === true;
  const shifts = useCollection('shifts', { enabled: activeProfile, filters: [{ field: 'currentEmployeeId', op: '==', value: user?.uid || '_' }] });
  const allTasks = useCollection('shiftTasks', { enabled: activeProfile, filters: [{ field: 'assignedEmployeeId', op: '==', value: user?.uid || '_' }] });
  const locations = useCollection('locations', { enabled: activeProfile });
  const shiftTypes = useCollection('shiftTypes', { enabled: activeProfile });
  // Handovers addressed to me (the security rules allow reading a handover only
  // when I'm its from/to employee).
  const incomingHandovers = useCollection('handovers', { enabled: activeProfile, filters: [{ field: 'toEmployeeId', op: '==', value: user?.uid || '_' }] });

  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sheetTask, setSheetTask] = useState(null);
  const [showHandover, setShowHandover] = useState(false);
  // Shifts being handed TO me: the shift's currentEmployeeId is still the other
  // person, so the plain shifts query above can't see it. The Worker serves it.
  const [incoming, setIncoming] = useState({ shifts: [], handovers: [] });
  const lock = useRef(false);

  useEffect(() => {
    if (!activeProfile || !user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/shifts/incoming-handover`, {
          method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: '{}',
        });
        const j = await r.json();
        if (alive && j) setIncoming({ shifts: j.shifts || [], handovers: j.handovers || [] });
      } catch { /* keep last */ }
    };
    load();
    const id = setInterval(load, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [activeProfile, user]);

  const shift = useMemo(() => {
    const own = selectEmployeeShift(shifts.docs);
    const OPEN = ['ready_to_open', 'active', 'opening', 'wake_up_pending', 'wake_up_confirmed', 'wake_up_escalated', 'handover_pending'];
    if (own && OPEN.includes(own.status)) return own;
    const inc = incoming.shifts.find((s) => s.status === 'handover_pending');
    return inc || own;
  }, [shifts.docs, incoming.shifts]);
  const tasks = useMemo(() => tasksForShift(allTasks.docs, shift?.id), [allTasks.docs, shift]);
  const locName = indexById(locations.docs)[shift?.locationId]?.name;
  const typeName = indexById(shiftTypes.docs)[shift?.shiftTypeId]?.name;

  const telegramLinked = typeof profile?.telegramChatId === 'string' && profile.telegramChatId.trim().length > 0;
  const blockers = tasks.filter((t) => t.blocksShiftClosing && t.status !== 'completed');
  const doneCount = tasks.filter((t) => t.status === 'completed').length;
  const action = primaryAction(shift);
  const onShift = shift?.currentEmployeeId === user?.uid;
  const handoverForMe = useMemo(
    () => [...incomingHandovers.docs, ...incoming.handovers].find((h) => h.shiftId === shift?.id && h.status === 'requested'),
    [incomingHandovers.docs, incoming.handovers, shift],
  );

  const refresh = useCallback(() => setError(null), []);
  useEffect(() => {
    const sub = AppState.addEventListener('change', (s) => { if (s === 'active') refresh(); });
    return () => sub.remove();
  }, [refresh]);

  const runCommand = async (key, path, body) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(key);
    setError(null);
    try {
      await sendTestWorkerCommand({ user, path, body, idempotencyKey: `emp:${user.uid}:${key}:${shift?.id || ''}` });
      if (key === 'open' && shift?.id) reportCheckInGeo(user, shift.id);
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      lock.current = false;
      setBusy(null);
    }
  };

  const completeTask = async (task, { draft = {}, photos = [] } = {}) => {
    const submissionId = `sub:${user.uid}:${task.id}`;
    // Fresh key per save so re-edited drafts don't collide; the completion — the
    // operation that matters — stays deterministic per task.
    await sendTestWorkerCommand({
      user,
      idempotencyKey: `emp:${user.uid}:save:${task.id}:${Date.now()}`,
      path: '/commands/save-task-draft',
      body: { submissionId, taskId: task.id, shiftId: shift.id, photoCount: draft.photoCount || 0, comment: draft.comment, amount: draft.amount },
    });
    const done = { user, idempotencyKey: `emp:${user.uid}:done:${task.id}`, path: '/commands/complete-task', body: { submissionId, photos } };
    await (photos.length ? photoCommandClient.send(done) : sendTestWorkerCommand(done));
  };

  const onTaskPress = async (task) => {
    if (task.status === 'completed') return;
    // Time-gate: a task can't be done before its window opens (e.g. the 2-hour
    // cash counts). `status` is only refreshed by the cron, so also check the
    // timestamp directly.
    const notYet = task.status === 'upcoming' || (task.availableAt && toMs(task.availableAt) > Date.now());
    if (notYet) {
      setError(`Эту задачу можно выполнить с ${hhmm(task.availableAt)}.`);
      return;
    }
    const req = task.requirements || {};
    if (req.photoRequired || req.amountRequired || req.commentRequired) { setSheetTask(task); return; }
    if (lock.current) return;
    lock.current = true;
    setBusy(task.id);
    setError(null);
    try {
      await completeTask(task);
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      lock.current = false;
      setBusy(null);
    }
  };

  if (!profile) return <Loader />;

  if (!activeProfile) {
    return (
      <View style={screen.root}>
        <ScrollView contentContainerStyle={screen.content}>
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.name}>{profile.fullName || 'Сотрудник'}</Text>
              <Text style={styles.sub}>Ожидание активации</Text>
            </View>
            <TextButton title="Выйти" kind="muted" onPress={logout} />
          </View>
          <Card>
            <Text style={styles.big}>Аккаунт на модерации ⏳</Text>
            <Text style={styles.sub}>
              Админ ещё не дал добро. Как включит доступ и назначит точку — здесь появятся твои смены и задачи.
            </Text>
          </Card>
          {telegramLinked ? <TelegramLinkedNote /> : <TelegramLinkCard user={user} />}
        </ScrollView>
      </View>
    );
  }

  if ((shifts.loading || allTasks.loading) && !shift) return <Loader />;

  return (
    <View style={screen.root}>
      <StickerBackdrop seed="myshift" opacity={0.12} />
      <ScrollView contentContainerStyle={screen.content}>
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text style={styles.name}>{profile?.fullName || 'Сотрудник'}</Text>
            <Text style={styles.sub}>{locName || 'Смена сотрудника'}</Text>
          </View>
          <TextButton title="Выйти" kind="muted" onPress={logout} />
        </View>

        <ErrorBanner message={error || shifts.error} onRetry={refresh} />

        {!shift ? (
          <Card style={styles.restCard}>
            <Sticker name="pechka" size={104} />
            <View style={styles.grow}>
              <Text style={styles.big}>Смен пока нет</Text>
              <Text style={styles.sub}>Отдыхаешь. Как админ поставит тебя в график — смена появится здесь.</Text>
            </View>
          </Card>
        ) : (
          <>
            <Card glow={shift.status === 'active'} accent={shift.status === 'active' ? 'rgba(0,210,160,0.35)' : undefined}>
              <Text style={styles.big}>{shiftStateLabel(shift.status)}</Text>
              <Text style={styles.sub}>
                {[typeName, hhmm(shift.plannedStartAt) && `${hhmm(shift.plannedStartAt)}–${hhmm(shift.plannedEndAt)}`].filter(Boolean).join(' · ')}
              </Text>
              {action && (
                <View style={{ marginTop: spacing.md }}>
                  <PrimaryButton
                    title={action.label}
                    loading={busy === action.key}
                    onPress={() => runCommand(action.key, action.path, { shiftId: shift.id })}
                  />
                </View>
              )}
            </Card>

            {shift.status === 'active' && (
              <>
                <View style={styles.progressRow}>
                  <SectionLabel>Задачи смены</SectionLabel>
                  <Text style={styles.progress}>{doneCount} / {tasks.length}</Text>
                </View>
                {tasks.map((t) => {
                  const notYet = t.status === 'upcoming' || (t.availableAt && toMs(t.availableAt) > Date.now());
                  return (
                    <Card key={t.id} onPress={() => onTaskPress(t)} style={[styles.task, notYet && styles.taskLocked]}>
                      <View style={styles.taskTop}>
                        <Text style={[styles.taskTitle, t.status === 'completed' && styles.taskDone, notYet && styles.taskDim]}>{t.order}. {t.title}</Text>
                        <Pill label={notYet ? `с ${hhmm(t.availableAt)}` : taskStateLabel(t.status)} kind={notYet ? 'muted' : taskKind(t.status)} />
                      </View>
                      <Text style={styles.taskMeta}>
                        {PHASE[t.phase] || t.phase}
                        {notYet ? ` · откроется в ${hhmm(t.availableAt)}` : (t.dueAt ? ` · до ${hhmm(t.dueAt)}` : '')}
                        {t.blocksShiftClosing ? ' · обязательная' : ''}
                      </Text>
                    </Card>
                  );
                })}
                {!tasks.length && <Text style={styles.sub}>Задачи подтянутся после открытия смены.</Text>}

                <PrimaryButton
                  title={blockers.length ? `Ещё ${blockers.length} обязательных` : 'Закрыть смену'}
                  loading={busy === 'close'}
                  disabled={blockers.length > 0}
                  onPress={() => runCommand('close', '/commands/request-close-shift', { shiftId: shift.id })}
                />
                {onShift && <GhostButton title="Передать смену" onPress={() => setShowHandover(true)} />}
              </>
            )}

            {shift.status === 'handover_pending' && handoverForMe && (
              <Card glow>
                <Text style={styles.big}>Тебе передают смену 🤝</Text>
                <Text style={styles.sub}>
                  Причина: {handoverForMe.reason}
                  {typeof handoverForMe.amount === 'number' ? ` · касса ${handoverForMe.amount} ₽` : ''}
                  {handoverForMe.heldReceiptsChecked ? ' · чеки проверены' : ''}
                </Text>
                {handoverForMe.heldReceiptsNote ? <Text style={styles.sub}>Заметка: {handoverForMe.heldReceiptsNote}</Text> : null}
                <View style={{ marginTop: spacing.md }}>
                  <PrimaryButton
                    title="Принять смену"
                    loading={busy === 'accept-handover'}
                    onPress={() => runCommand('accept-handover', '/commands/accept-handover', { shiftId: shift.id, handoverId: handoverForMe.id })}
                  />
                </View>
              </Card>
            )}

            {shift.status === 'handover_pending' && !handoverForMe && onShift && (
              <Card>
                <Text style={styles.big}>Ждём сменщика ⏳</Text>
                <Text style={styles.sub}>Смена передана. Она остаётся на тебе, пока сменщик не подтвердит приём со своего телефона.</Text>
              </Card>
            )}

            {isShiftTerminal(shift.status) && (
              <Text style={styles.sub}>
                {shift.status === 'closed' ? 'Смена закрыта. Красава 🙌'
                  : shift.status === 'force_closed' ? 'Смену закрыл админ.'
                  : shift.status === 'missed' ? 'Смена пропущена 😕'
                  : 'Смена отменена.'}
              </Text>
            )}
          </>
        )}

        {telegramLinked ? <TelegramLinkedNote /> : <TelegramLinkCard user={user} />}
      </ScrollView>

      {sheetTask && (
        <TaskSheet
          task={sheetTask}
          onClose={() => setSheetTask(null)}
          onComplete={(payload) => completeTask(sheetTask, payload)}
        />
      )}

      {showHandover && shift && (
        <HandoverSheet
          user={user}
          shift={shift}
          onClose={(done) => { setShowHandover(false); if (done) refresh(); }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center' },
  name: { ...typography.h2, color: colors.textPrimary },
  sub: { ...typography.body, color: colors.textSecondary, marginTop: 2 },
  big: { ...typography.h3, color: colors.textPrimary },
  restCard: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  grow: { flex: 1 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: spacing.sm },
  progress: { ...typography.button, fontSize: 14, color: colors.primary, fontVariant: ['tabular-nums'] },
  task: { gap: 6 },
  taskLocked: { opacity: 0.55 },
  taskTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  taskTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14, flex: 1 },
  taskDone: { color: colors.textMuted, textDecorationLine: 'line-through' },
  taskDim: { color: colors.textSecondary },
  taskMeta: { ...typography.caption, color: colors.textMuted },
});
